import React, { useState, useMemo, useEffect, useRef } from 'react';
import SafeIcon from './SafeIcon';
import * as FiIcons from 'react-icons/fi';
import snapChartLogo from '@/assets/snap-chart-logo.png';
import { AnalysisQuickSummary, ChartReadabilityError, parseAnalysisDecision } from './analysis';
import ShareAnalysisCard from './analysis/ShareAnalysisCard';
import MarkdownText from './MarkdownText';

const { FiCopy, FiCheck, FiTarget, FiAlertCircle, FiBookOpen, FiPlus, FiActivity, FiEdit3, FiUpload, FiClipboard, FiTrash2, FiLayers, FiAlertTriangle, FiZap, FiShield } = FiIcons;

const AnalysisResult = ({ analysisResult, analyzedChartImage, isLoading, error, onExtractTradeLevels, onOpenPromptEditor, onClearAnalysis, onRetryAnalysis }) => {
  const [copied, setCopied] = useState(false);
  const [showFullAnalysis, setShowFullAnalysis] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [uploadedImages, setUploadedImages] = useState([]);
  const containerRef = useRef(null);
  const [captureNotification, setCaptureNotification] = useState(null);
  const [toast, setToast] = useState(null);

  // Custom branded toast notification
  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  // 📸 Listen for capture requests from the chart
  useEffect(() => {
    const handleCaptureRequest = (event) => {
      const { symbol, symbolName, timeframe, market } = event.detail;
      console.log('📸 Capture request received:', event.detail);
      
      // Show notification
      setCaptureNotification({
        symbol,
        symbolName,
        timeframe,
        market,
        timestamp: Date.now()
      });
      
      // Scroll to this component
      if (containerRef.current) {
        containerRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        containerRef.current.focus();
      }
      
      // Auto-hide after 8 seconds
      setTimeout(() => {
        setCaptureNotification(null);
      }, 8000);
    };
    
    window.addEventListener('chart-capture-request', handleCaptureRequest);
    return () => window.removeEventListener('chart-capture-request', handleCaptureRequest);
  }, []);

  const handleCopy = () => {
    const textToCopy = analysisResult?.analysis_result || analysisResult?.text || JSON.stringify(analysisResult, null, 2);
    if (textToCopy) {
      navigator.clipboard.writeText(textToCopy);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  // ✅ EXTRACTION ULTRA-ROBUSTE SPÉCIFIQUEMENT POUR VOTRE FORMAT
  const parseTradeIdeas = (rawText) => {
    if (!rawText || typeof rawText !== 'string') {
      console.log('❌ No valid text provided for parsing');
      return null;
    }

    // ✅ Strip bold markers globally for consistent parsing (Gemini wraps everything in **)
    const text = rawText.replace(/\*\*/g, '');

    // ✅ EARLY CHECK: If this is a WAIT or NO_TRADE decision, don't extract trade levels
    const isWaitOrNoTrade = /⏳\s*WAIT/i.test(text) || 
                            /WAIT\s*[—–\-−]+\s*STRUCTURE/i.test(text) ||
                            /❌\s*NO-TRADE/i.test(text) ||
                            /NO-TRADE\s*[—–-]/i.test(text) ||
                            (/WAIT/i.test(text) && /STRUCTURE\s*NOT/i.test(text)) ||
                            /WATCH\s*[-–—]\s*MONITORING/i.test(text) ||
                            /WATCH\s*[-–—]\s*OBSERVE/i.test(text) ||
                            /WATCH_MONITORING/i.test(text) ||
                            /Decision:\s*(WAIT|NO_TRADE|WATCH)/i.test(text);
    
    if (isWaitOrNoTrade) {
      console.log('⏳ WAIT/NO_TRADE decision detected - skipping trade level extraction');
      return null;
    }

    console.log('🔍 ULTRA DEBUG - Full text to parse:');
    console.log('=' * 100);
    console.log(text);
    console.log('=' * 100);

    // ✅ PATTERNS ULTRA-ROBUSTES pour tous les formats (GPT + Gemini)
    const directionPatterns = [
      /Direction[:\s]+\s*(LONG|SHORT|BUY|SELL)/i,
    ];

    // ✅ PATTERNS AMÉLIORÉS pour capturer les prix (bold already stripped)
    const entryPatterns = [
      /Entry\s*(?:Price)?[:\s]+\$([0-9,]+(?:\.[0-9]+)?)/i,
      /Entry\s*Zone[:\s]+\$([0-9,]+(?:\.[0-9]+)?)/i,
      /Entry[:\s]+\$([0-9,]+(?:\.[0-9]+)?)/i,
      /BUY STOP.*?\$([0-9,]+(?:\.[0-9]+)?)/i,
      /SELL LIMIT.*?\$([0-9,]+(?:\.[0-9]+)?)/i,
      /BUY LIMIT.*?\$([0-9,]+(?:\.[0-9]+)?)/i,
      /SELL STOP.*?\$([0-9,]+(?:\.[0-9]+)?)/i
    ];
    
    const slPatterns = [
      /Stop Loss(?:\s*\(SL\))?[:\s]+\$([0-9,]+(?:\.[0-9]+)?)/i,
      /SL[:\s]+\$([0-9,]+(?:\.[0-9]+)?)/i,
    ];
    
    const tp1Patterns = [
      /Take Profit 1(?:\s*\(TP1\))?[:\s]+\$([0-9,]+(?:\.[0-9]+)?)/i,
      /TP1[:\s]+\$([0-9,]+(?:\.[0-9]+)?)/i,
    ];
    
    const tp2Patterns = [
      /Take Profit 2(?:\s*\(TP2\))?[:\s]+\$([0-9,]+(?:\.[0-9]+)?)/i,
      /TP2[:\s]+\$([0-9,]+(?:\.[0-9]+)?)/i,
    ];
    
    const tp3Patterns = [
      /Take Profit 3(?:\s*\(TP3\))?[:\s]+\$([0-9,]+(?:\.[0-9]+)?)/i,
      /TP3[:\s]+\$([0-9,]+(?:\.[0-9]+)?)/i,
    ];

    const confidencePatterns = [
      /Confidence(?:\s*Score)?[:\s]+([0-9]+)%?/i,
      /Probability of Success[:\s]+([0-9]+)%?/i,
      /Setup Quality Score[:\s]+([0-9.]+)/i
    ];

    // ✅ FONCTION D'EXTRACTION AVEC DEBUG ULTRA-DÉTAILLÉ
    const extractValue = (patterns, label) => {
      console.log(`🔍 Searching for ${label}:`);
      
      for (let i = 0; i < patterns.length; i++) {
        const pattern = patterns[i];
        console.log(`  Pattern ${i + 1}: ${pattern}`);
        
        const match = text.match(pattern);
        if (match) {
          const value = match[1].replace(/,/g, '');
          console.log(`  ✅ MATCH FOUND! Pattern ${i + 1} matched: "${match[0]}" -> Value: "${value}"`);
          return value;
        } else {
          console.log(`  ❌ No match for pattern ${i + 1}`);
        }
      }
      
      console.log(`  ❌ NO MATCHES FOUND for ${label}`);
      return 'N/A';
    };

    // ✅ EXTRAIRE TOUS LES NIVEAUX AVEC DEBUG
    console.log('\n🎯 STARTING EXTRACTION:');
    
    const direction = extractValue(directionPatterns, 'DIRECTION');
    const entry = extractValue(entryPatterns, 'ENTRY');
    const stopLoss = extractValue(slPatterns, 'STOP LOSS');
    const tp1 = extractValue(tp1Patterns, 'TP1');
    const tp2 = extractValue(tp2Patterns, 'TP2');
    const tp3 = extractValue(tp3Patterns, 'TP3');
    const confidence = extractValue(confidencePatterns, 'CONFIDENCE');

    // ✅ VALIDATION
    if (entry === 'N/A') {
      console.log('❌ CRITICAL: No entry price found, cannot create trade setup');
      return null;
    }

    const result = {
      entry: entry,
      stopLoss: stopLoss,
      tp1: tp1,
      tp2: tp2, 
      tp3: tp3,
      direction: direction,
      confidence: confidence !== 'N/A' ? parseInt(confidence) : 80
    };

    console.log('🎯 FINAL EXTRACTED RESULT:', result);
    return result;
  };

  const analysisText = useMemo(() => {
    if (!analysisResult) return null;
    const text = analysisResult.analysis_result || analysisResult.text || analysisResult.content || analysisResult.response || JSON.stringify(analysisResult, null, 2);
    console.log('📋 Analysis text extracted:', { 
      hasText: !!text, 
      length: text?.length, 
      preview: text?.substring(0, 200) 
    });
    return text;
  }, [analysisResult]);

  const tradeLevels = useMemo(() => {
    console.log('🔄 Re-calculating trade levels...');
    const levels = parseTradeIdeas(analysisText);
    console.log('📊 Trade levels result:', levels);
    return levels;
  }, [analysisText]);

  // ✅ Parse structured decision from analysis
  const analysisDecision = useMemo(() => {
    if (!analysisText) return null;
    const decision = parseAnalysisDecision(analysisText);
    console.log('📋 Analysis decision parsed:', decision);
    return decision;
  }, [analysisText]);

  // ✅ Detect if the final decision is NO_TRADE or WAIT (hide Trade Setup card)
  const isNoTrade = useMemo(() => {
    if (!analysisText) return false;
    
    // Use the parsed decision status
    if (analysisDecision?.status === 'NO_TRADE' || analysisDecision?.status === 'WAIT') {
      console.log('🚫 NO_TRADE/WAIT decision detected via parser - hiding Trade Setup card');
      return true;
    }
    
    // Fallback: Check for explicit NO-TRADE or WAIT patterns
    const noTradePatterns = [
      /❌\s*NO-TRADE/i,
      /NO-TRADE\s*[—–-]\s*CONDITIONS/i,
      /⏳\s*WAIT/i,
      /WAIT\s*[—–\-−]+\s*STRUCTURE/i,
      /WAIT\s*[—–\-−]+\s*CONDITIONS/i,
      /WAIT\s*–\s*STRUCTURE\s*NOT\s*CONFIRMED/i,
      /Final Trade Authorization[\s\S]*?NO-TRADE/i,
      /Trade Authorization[\s\S]*?❌/i,
      /Trade Authorization[\s\S]*?⏳/i,
      /Trade Authorization[\s\S]*?WAIT/i,
      /EXECUTION_DECISION_BLOCK[\s\S]*?Decision:\s*(NO_TRADE|WAIT|WATCH)/i,
      /WATCH\s*[-–—]\s*MONITORING/i,
      /WATCH\s*[-–—]\s*OBSERVE/i,
      /WATCH_MONITORING/i,
      /Decision:\s*(WAIT|NO_TRADE|WATCH)/i,
    ];
    
    for (const pattern of noTradePatterns) {
      if (pattern.test(analysisText)) {
        console.log('🚫 NO_TRADE/WAIT decision detected - hiding Trade Setup card');
        return true;
      }
    }
    
    // Additional check: if text contains "WAIT" with "STRUCTURE NOT" nearby
    if (/WAIT/i.test(analysisText) && /STRUCTURE\s*NOT/i.test(analysisText)) {
      console.log('🚫 WAIT+STRUCTURE NOT pattern detected - hiding Trade Setup card');
      return true;
    }
    
    return false;
  }, [analysisText, analysisDecision]);

  // ✅ Detect if this is a model refusal/error that needs special handling
  const isChartReadabilityError = useMemo(() => {
    return analysisDecision?.status === 'ERROR' && 
           (analysisDecision?.errorCode === 'MODEL_REFUSAL' || 
            analysisDecision?.errorCode === 'INSUFFICIENT_RESPONSE' ||
            analysisDecision?.errorCode === 'CHART_UNREADABLE');
  }, [analysisDecision]);

  useEffect(() => {
    if (tradeLevels && onExtractTradeLevels) {
      console.log('📤 Sending trade levels to parent:', tradeLevels);
      onExtractTradeLevels(tradeLevels);
    }
  }, [tradeLevels, onExtractTradeLevels]);

  // ✅ GESTION CTRL+V POUR PLUSIEURS IMAGES
  useEffect(() => {
    const handlePaste = async (e) => {
      console.log('📋 Paste event detected:', e);
      
      if (!containerRef.current?.contains(e.target)) {
        console.log('📋 Paste not in AnalysisResult container');
        return;
      }

      const items = e.clipboardData?.items;
      if (!items) {
        console.log('📋 No clipboard items found');
        return;
      }

      console.log('📋 Clipboard items:', Array.from(items).map(item => ({ type: item.type, kind: item.kind })));

      const imageItems = [];
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (item.type.startsWith('image/')) {
          imageItems.push(item);
        }
      }

      if (imageItems.length > 0) {
        console.log(`📋 ${imageItems.length} images found in clipboard`);
        e.preventDefault();
        
        for (let i = 0; i < imageItems.length; i++) {
          const item = imageItems[i];
          const blob = item.getAsFile();
          if (blob) {
            console.log(`📋 Processing image ${i + 1}:`, { size: blob.size, type: blob.type });
            await processImageFile(blob, `SnapChart-Analysis-${Date.now()}-${i + 1}.png`);
          }
        }
        return;
      }
      
      console.log('📋 No images found in clipboard');
    };

    document.addEventListener('paste', handlePaste);
    
    return () => {
      document.removeEventListener('paste', handlePaste);
    };
  }, []);

  const processImageFile = async (file, fileName = null) => {
    try {
      console.log('🖼️ Processing image file:', { name: fileName || file.name, size: file.size, type: file.type });

      if (!file.type.startsWith('image/')) {
        showToast('Please select an image file', 'error');
        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        showToast('Image size must be less than 5MB', 'error');
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        const imageData = e.target.result;
        
        console.log('✅ Image processed successfully:', {
          fileName: fileName || file.name,
          size: file.size,
          dataLength: imageData.length
        });

        const newImage = {
          id: Date.now() + Math.random(),
          data: imageData,
          name: fileName || file.name,
          size: file.size,
          type: file.type,
          timestamp: new Date().toLocaleString()
        };

        // Add image to state - the useEffect will dispatch the event after state updates
        setUploadedImages(prev => {
          const updatedImages = [...prev, newImage];
          // Use setTimeout to dispatch event after state update
          setTimeout(() => {
            const event = new CustomEvent('uploadChartImage', {
              detail: {
                imageData,
                fileName: fileName || file.name,
                fileSize: file.size,
                fileType: file.type,
                totalImages: updatedImages.length,
                allImages: updatedImages
              },
              bubbles: true
            });
            window.dispatchEvent(event);
            console.log('📤 Dispatched uploadChartImage event with', updatedImages.length, 'images');
          }, 0);
          return updatedImages;
        });
        
        showToast(`Chart uploaded: ${fileName || file.name}`, 'success');
      };

      reader.onerror = (error) => {
        console.error('❌ Error reading image file:', error);
        showToast('Error processing image file', 'error');
      };

      reader.readAsDataURL(file);
      
    } catch (error) {
      console.error('❌ Error processing image:', error);
      showToast('Error processing image', 'error');
    }
  };

  const handleImageUpload = (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    console.log(`📁 Processing ${files.length} files from input`);
    
    files.forEach((file, index) => {
      processImageFile(file, `SnapChart-Upload-${Date.now()}-${index + 1}.png`);
    });

    event.target.value = '';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    
    const files = Array.from(e.dataTransfer.files);
    const imageFiles = files.filter(file => file.type.startsWith('image/'));
    
    if (imageFiles.length > 0) {
      console.log(`🎯 Dropped ${imageFiles.length} image files`);
      imageFiles.forEach((file, index) => {
        processImageFile(file, `SnapChart-Drop-${Date.now()}-${index + 1}.png`);
      });
    } else {
      showToast('Please drop image files only', 'error');
    }
  };

  const removeImage = (imageId) => {
    setUploadedImages(prev => prev.filter(img => img.id !== imageId));
    console.log(`🗑️ Image removed: ${imageId}`);
  };

  const clearAllImages = () => {
    if (uploadedImages.length === 0) return;
    
    const confirmClear = window.confirm(`Supprimer toutes les ${uploadedImages.length} images ?`);
    if (confirmClear) {
      setUploadedImages([]);
      console.log('🧹 All images cleared');
    }
  };

  // ✅ FONCTION DÉSACTIVÉE TEMPORAIREMENT AVEC MESSAGE CLAIR
  const handleAddToJournal = () => {
    if (!tradeLevels || !tradeLevels.entry || tradeLevels.entry === 'N/A') {
      alert('❌ Entry price not found in analysis. Cannot add to journal.');
      return;
    }

    // ✅ DÉSACTIVÉ TEMPORAIREMENT - AFFICHER MESSAGE CLAIR
    alert(`🚧 JOURNAL FUNCTION TEMPORARILY DISABLED

❌ There are database connection issues preventing the trading journal from working properly.

📋 **Your Trade Setup:**
• Direction: ${tradeLevels.direction}
• Entry: $${tradeLevels.entry}
• Stop Loss: $${tradeLevels.stopLoss}
• TP1: $${tradeLevels.tp1}
• TP2: $${tradeLevels.tp2}
• TP3: $${tradeLevels.tp3}
• Confidence: ${tradeLevels.confidence}%

📝 **Manual Solution:**
Copy these levels manually to your trading platform or external journal.

🔧 This feature will be restored once database issues are resolved.`);
    
    return; // Ne pas essayer d'envoyer au journal pour le moment
  };

  if (isLoading) {
    return (
      <div className="bg-gray-900/60 rounded-xl border border-white/10 p-8">
        <div className="text-center">
          <div className="mb-5 w-fit mx-auto">
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/30 to-purple-500/30 rounded-xl blur-2xl opacity-70 animate-pulse"></div>
              <div className="relative p-4 rounded-xl bg-gradient-to-br from-cyan-500/15 to-purple-500/15 border border-cyan-400/30 animate-spin" style={{ animationDuration: '2s' }}>
                <SafeIcon icon={FiShield} className="text-3xl text-cyan-300" />
              </div>
            </div>
          </div>
          <span className="text-[10px] font-mono uppercase tracking-[0.25em] text-cyan-400">▸ ai working</span>
          <h3 className="text-xl font-black text-white mt-1 mb-3">Snap Chart AI Analysis</h3>
          {/* Multi-step progress (visual only — gives the user a sense of what's happening) */}
          <div className="max-w-xs mx-auto space-y-2 text-left">
            {[
              { label: 'Reading chart structure', delay: '0s' },
              { label: 'Checking liquidity zones', delay: '0.6s' },
              { label: 'Computing verdict', delay: '1.2s' },
            ].map((s, i) => (
              <div key={i} className="flex items-center gap-2 text-xs text-gray-400 font-mono">
                <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-pulse" style={{ animationDelay: s.delay }} />
                <span>{s.label}…</span>
              </div>
            ))}
          </div>
          {uploadedImages.length > 0 && (
            <div className="mt-4">
              <p className="text-blue-400 text-sm">📊 {uploadedImages.length} chart image(s) included</p>
              <div className="grid grid-cols-3 gap-2 mt-2">
                {uploadedImages.slice(0, 3).map((img, index) => (
                  <div key={img.id} className="text-xs text-blue-300 truncate">
                    {index + 1}. {img.name}
                  </div>
                ))}
                {uploadedImages.length > 3 && (
                  <div className="text-xs text-blue-300">+{uploadedImages.length - 3} more...</div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-gray-800 rounded-xl border border-gray-700 p-8">
        <div className="text-center">
          <SafeIcon icon={FiAlertCircle} className="text-4xl text-red-400 mb-4 mx-auto" />
          <h3 className="text-lg font-bold text-red-400 mb-2">Analysis Error</h3>
          <p className="text-red-300 text-sm mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg"
          >
            Retry Analysis
          </button>
        </div>
      </div>
    );
  }

  if (!analysisResult || !analysisText) {
    return (
      <div 
        ref={containerRef}
        className={`bg-gray-900/80 backdrop-blur-sm rounded-xl border border-white/5 p-6 transition-all duration-300 h-full flex flex-col ${
          dragOver ? 'border-purple-500/70 bg-purple-900/20' : ''
        }`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        tabIndex={-1}
      >
        <div className="text-center">
          {/* v3 Clean drop zone — single icon, mono uppercase eyebrow, no tutorial fluff */}
          <div className="mb-5 w-fit mx-auto">
            <div className="w-16 h-16 rounded-xl border-2 border-dashed border-white/15 bg-white/[0.02] flex items-center justify-center transition group-hover:border-cyan-400/50 group-hover:bg-cyan-500/5">
              <SafeIcon icon={FiIcons.FiCamera} className="w-7 h-7 text-gray-500" />
            </div>
          </div>

          <div className="text-center mb-5">
            <span className="text-[10px] font-mono uppercase tracking-[0.25em] text-cyan-400">▸ drop a chart</span>
            <h3 className="text-2xl font-black text-white mt-1">
              Get a verdict in <span className="text-cyan-400">2 seconds</span>
            </h3>
            <p className="text-xs text-gray-500 mt-1.5 font-mono uppercase tracking-wider">
              ctrl+v · drag-drop · or click below
            </p>
          </div>

          {/* Capture notification — keep but redesigned v3 */}
          {captureNotification && (
            <div className="mb-5 animate-fade-in">
              <div className="rounded-xl border border-cyan-400/30 bg-cyan-500/5 p-3">
                <div className="flex items-center justify-center gap-2 mb-1.5">
                  <span className="w-2 h-2 rounded-full bg-cyan-400 animate-pulse" />
                  <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-cyan-300">ready to receive</span>
                </div>
                <p className="text-xs text-gray-300">
                  Capture <strong className="text-white">{captureNotification.symbolName}</strong> ({captureNotification.timeframe}) → paste here
                </p>
              </div>
            </div>
          )}
          
          {uploadedImages.length > 0 && (
            <div className="mb-6">
              <div className="bg-green-900/30 rounded-lg p-4 border border-green-600/50">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center space-x-2">
                    <SafeIcon icon={FiCheck} className="text-green-400" />
                    <span className="text-green-400 font-semibold">
                      {uploadedImages.length} Chart{uploadedImages.length > 1 ? 's' : ''} Ready
                    </span>
                  </div>
                  <button
                    onClick={clearAllImages}
                    className="text-xs text-red-400 hover:text-red-300 flex items-center space-x-1"
                  >
                    <SafeIcon icon={FiTrash2} className="w-3 h-3" />
                    <span>Clear All</span>
                  </button>
                </div>
                
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-3">
                  {uploadedImages.map((img, index) => (
                    <div key={img.id} className="relative group">
                      <img 
                        src={img.data} 
                        alt={`Chart ${index + 1}`}
                        className="w-full h-20 object-cover rounded border border-green-500"
                      />
                      <div className="absolute inset-0 bg-black bg-opacity-50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                        <button
                          onClick={() => removeImage(img.id)}
                          className="text-red-400 hover:text-red-300"
                        >
                          <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                        </button>
                      </div>
                      <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-70 text-white text-xs p-1 truncate">
                        {index + 1}. {img.name} ({Math.round(img.size / 1024)}KB)
                      </div>
                    </div>
                  ))}
                </div>

                <div className="text-xs text-green-300 space-y-1">
                  <p>📊 Total Size: {Math.round(uploadedImages.reduce((sum, img) => sum + img.size, 0) / 1024)}KB</p>
                  <p>🕒 Last Upload: {uploadedImages[uploadedImages.length - 1]?.timestamp}</p>
                </div>
              </div>
            </div>
          )}
          
          
          <div className="mt-auto pt-8">
            <input
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              multiple
              className="hidden"
              id="chart-image-upload"
            />
            <button
              onClick={() => document.getElementById('chart-image-upload').click()}
              className="w-full py-3.5 px-6 rounded-xl bg-gradient-to-r from-cyan-400 to-purple-400 text-black font-bold text-sm hover:scale-[1.01] transition shadow-xl shadow-cyan-500/20 flex items-center justify-center gap-2"
            >
              <SafeIcon icon={FiUpload} className="w-4 h-4" />
              <span>Upload Chart Images</span>
              {uploadedImages.length > 0 && (
                <span className="bg-black/20 text-black text-xs px-2 py-0.5 rounded-full font-bold">
                  {uploadedImages.length}
                </span>
              )}
            </button>
            <p className="text-[10px] text-gray-500 text-center mt-3 font-mono uppercase tracking-wider">
              up to 10 charts · max 5MB each · PNG / JPG / GIF
            </p>
          </div>
          
          {dragOver && (
            <div className="absolute inset-0 bg-blue-500/20 border-2 border-dashed border-blue-400 rounded-xl flex items-center justify-center">
              <div className="text-center">
                <SafeIcon icon={FiUpload} className="text-4xl text-blue-400 mb-2 mx-auto" />
                <p className="text-blue-400 font-semibold">Drop your chart images here!</p>
                <p className="text-blue-300 text-sm">Multiple files supported</p>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ✅ Handle Chart Readability Error - Show special error UI
  if (isChartReadabilityError && analysisDecision) {
    return (
      <div 
        ref={containerRef}
        className="bg-gray-800 rounded-xl border border-gray-700"
      >
        <ChartReadabilityError 
          errorCode={analysisDecision.errorCode}
          uiMessage={analysisDecision.uiMessage}
          onRetry={onRetryAnalysis}
          onUploadNew={() => {
            if (onClearAnalysis) onClearAnalysis();
            setUploadedImages([]);
          }}
        />
      </div>
    );
  }

  return (
    <>
      <div 
        ref={containerRef}
        className={`bg-gray-800 rounded-xl border border-gray-700 transition-all duration-300 ${
          dragOver ? 'border-blue-500 bg-blue-900/10' : ''
        }`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        tabIndex={0}
      >
        
        <div className="p-4 border-b border-gray-700 flex justify-between items-center">
          <div className="flex items-center space-x-3">
            {/* Logo style navbar */}
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-purple-600/30 to-cyan-600/30 rounded-xl blur-lg opacity-50"></div>
              <div className="relative p-2 rounded-xl bg-gradient-to-r from-purple-600/20 to-cyan-600/20 border border-purple-500/30">
                <SafeIcon icon={FiShield} className="text-xl text-cyan-400" />
              </div>
            </div>
            <h3 className="text-lg font-bold bg-gradient-to-r from-white to-cyan-400 bg-clip-text text-transparent">
              Snap Chart AI Analysis
            </h3>
            {uploadedImages.length > 0 && (
              <span className="bg-gradient-to-r from-purple-600/20 to-cyan-600/20 text-cyan-400 text-xs px-2 py-1 rounded-full border border-cyan-500/30 flex items-center space-x-1">
                <SafeIcon icon={FiLayers} className="w-3 h-3" />
                <span>{uploadedImages.length} Chart{uploadedImages.length > 1 ? 's' : ''}</span>
              </span>
            )}
          </div>
          {uploadedImages.length > 0 && (
            <button
              onClick={() => {
                setUploadedImages([]);
                if (onClearAnalysis) onClearAnalysis();
                console.log('🧹 All charts cleared + analysis reset');
              }}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-red-400 hover:text-red-300 bg-red-500/10 hover:bg-red-500/20 border border-red-500/20 rounded-lg transition-colors"
            >
              <SafeIcon icon={FiTrash2} className="w-3.5 h-3.5" />
              <span>Clear Charts</span>
            </button>
          )}
        </div>

        {/* ✅ QUICK SUMMARY CARD - Always shown at top (even for WAIT/NO_TRADE) */}
        {analysisText && !isChartReadabilityError && (
          <div className="p-4 border-b border-gray-700">
            <AnalysisQuickSummary analysisText={analysisText} />
          </div>
        )}

        {uploadedImages.length > 0 && (
          <div className="p-4 border-b border-gray-700">
            <h4 className="text-sm font-semibold text-white mb-3 flex items-center">
              <SafeIcon icon={FiLayers} className="mr-2 text-blue-400" />
              📊 Charts Analyzed ({uploadedImages.length})
            </h4>
            
            <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
              {uploadedImages.map((img, index) => (
                <div key={img.id} className="relative group">
                  <img 
                    src={img.data} 
                    alt={`Chart ${index + 1}`}
                    className="w-full h-16 object-cover rounded border border-gray-600 group-hover:border-blue-400 transition-colors"
                  />
                  <div className="absolute inset-0 bg-black bg-opacity-50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <button
                      onClick={() => removeImage(img.id)}
                      className="text-red-400 hover:text-red-300 bg-black bg-opacity-70 rounded p-1"
                    >
                      <SafeIcon icon={FiTrash2} className="w-3 h-3" />
                    </button>
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-80 text-white text-xs p-1 text-center">
                    #{index + 1}
                  </div>
                </div>
              ))}
            </div>
            
            <div className="mt-2 text-xs text-gray-400 flex justify-between">
              <span>Total: {Math.round(uploadedImages.reduce((sum, img) => sum + img.size, 0) / 1024)}KB</span>
              <span>AI analyzed {uploadedImages.length} chart{uploadedImages.length > 1 ? 's' : ''}</span>
            </div>
          </div>
        )}

        {/* ✅ TRADING SETUP - STYLE 2026 COMPACT - Hidden on NO_TRADE decisions */}
        {tradeLevels?.entry && tradeLevels.entry !== 'N/A' && !isNoTrade && (
          <div className="p-3 border-b border-white/5">
            <div className="relative overflow-hidden rounded-xl border border-white/10 bg-gradient-to-br from-gray-900/80 via-gray-800/60 to-gray-900/80 backdrop-blur-xl">
              {/* Subtle gradient overlay */}
              <div className="absolute inset-0 bg-gradient-to-r from-emerald-500/5 via-transparent to-cyan-500/5 pointer-events-none" />
              
              <div className="relative p-4">
                {/* Header compact */}
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-emerald-400 to-cyan-500 flex items-center justify-center">
                      <SafeIcon icon={FiTarget} className="w-3.5 h-3.5 text-white" />
                    </div>
                    <h4 className="text-sm font-semibold text-white">Trade Setup</h4>
                  </div>
                  <div className={`px-2.5 py-1 rounded-full text-xs font-semibold ${
                    tradeLevels.direction === 'LONG' 
                      ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' 
                      : 'bg-red-500/20 text-red-400 border border-red-500/30'
                  }`}>
                    {tradeLevels.direction}
                  </div>
                </div>
                
                {/* Main Levels Grid - Compact 2x3 */}
                <div className="grid grid-cols-3 gap-2 mb-3">
                  {/* Entry */}
                  <div className="bg-black/30 rounded-lg p-2.5 border border-cyan-500/20 text-center">
                    <div className="text-[10px] text-gray-400 uppercase tracking-wider mb-0.5">Entry</div>
                    <div className="font-bold text-base text-cyan-400 tabular-nums">${tradeLevels.entry}</div>
                  </div>
                  
                  {/* Stop Loss */}
                  <div className="bg-black/30 rounded-lg p-2.5 border border-red-500/20 text-center">
                    <div className="text-[10px] text-gray-400 uppercase tracking-wider mb-0.5">Stop Loss</div>
                    <div className="font-bold text-base text-red-400 tabular-nums">
                      {tradeLevels.stopLoss !== 'N/A' ? `$${tradeLevels.stopLoss}` : 'N/A'}
                    </div>
                  </div>
                  
                  {/* Confidence */}
                  <div className="bg-black/30 rounded-lg p-2.5 border border-amber-500/20 text-center">
                    <div className="text-[10px] text-gray-400 uppercase tracking-wider mb-0.5">Confidence</div>
                    <div className="font-bold text-base text-amber-400">{tradeLevels.confidence}%</div>
                  </div>
                </div>
                
                {/* Take Profits - Compact row */}
                <div className="grid grid-cols-3 gap-2">
                  <div className="bg-black/30 rounded-lg p-2.5 border border-emerald-500/20 text-center">
                    <div className="text-[10px] text-gray-400 uppercase tracking-wider mb-0.5">TP1</div>
                    <div className="font-bold text-base text-emerald-400 tabular-nums">
                      {tradeLevels.tp1 !== 'N/A' ? `$${tradeLevels.tp1}` : '-'}
                    </div>
                  </div>
                  <div className="bg-black/30 rounded-lg p-2.5 border border-emerald-500/20 text-center">
                    <div className="text-[10px] text-gray-400 uppercase tracking-wider mb-0.5">TP2</div>
                    <div className="font-bold text-base text-emerald-400 tabular-nums">
                      {tradeLevels.tp2 !== 'N/A' ? `$${tradeLevels.tp2}` : '-'}
                    </div>
                  </div>
                  <div className="bg-black/30 rounded-lg p-2.5 border border-emerald-500/20 text-center">
                    <div className="text-[10px] text-gray-400 uppercase tracking-wider mb-0.5">TP3</div>
                    <div className="font-bold text-base text-emerald-400 tabular-nums">
                      {tradeLevels.tp3 !== 'N/A' ? `$${tradeLevels.tp3}` : '-'}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="p-4 border-b border-gray-700">
          <button
            onClick={() => setShowFullAnalysis(true)}
            className="w-full px-4 py-3 rounded-xl bg-gray-800/80 border border-purple-500/30 hover:bg-gray-700/80 hover:border-purple-500/50 transition-all flex items-center justify-center gap-3"
          >
            <div className="p-1.5 rounded-lg bg-gradient-to-r from-purple-600/30 to-cyan-600/30">
              <SafeIcon icon={FiBookOpen} className="w-5 h-5 text-cyan-400" />
            </div>
            <span className="font-semibold text-white">Read Complete Professional Analysis</span>
          </button>
        </div>

        <div className="p-4">
          <div className="bg-gray-900/60 rounded-xl p-5 border border-white/10 min-h-[400px]">
            <div className="max-h-[400px] overflow-y-auto pr-2">
              <MarkdownText text={analysisText.substring(0, 1500)} />
              {analysisText.length > 1500 && (
                <div className="mt-4 pt-3 border-t border-white/5 text-center">
                  <button onClick={() => setShowFullAnalysis(true)}
                    className="text-xs font-mono uppercase tracking-[0.15em] text-cyan-400 hover:text-cyan-300 transition">
                    ▸ read complete analysis ({analysisText.length - 1500} more chars)
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="p-4 border-t border-gray-700 flex justify-between items-center">
          {/* Bouton style dashboard */}
          <div className="flex items-center gap-2">
            <button
              onClick={handleCopy}
              className="px-4 py-2.5 rounded-xl bg-gray-800/80 border border-purple-500/30 hover:bg-gray-700/80 hover:border-purple-500/50 transition-all flex items-center gap-2"
            >
              <div className="p-1.5 rounded-lg bg-gradient-to-r from-purple-600/30 to-cyan-600/30">
                <SafeIcon icon={copied ? FiCheck : FiCopy} className="w-4 h-4 text-cyan-400" />
              </div>
              <span className="text-sm font-medium text-white">{copied ? 'Copied!' : 'Copy Analysis'}</span>
            </button>
            {!isChartReadabilityError && analysisDecision && (
              <ShareAnalysisCard
                decision={analysisDecision}
                analysisResult={analysisResult}
                chartImage={analyzedChartImage || uploadedImages[0]?.data}
              />
            )}
          </div>

          {/* Info badges style dashboard */}
          <div className="flex items-center space-x-3">
            <div className="px-3 py-1.5 rounded-lg bg-gray-800/60 border border-white/10 text-xs text-gray-300 flex items-center gap-1.5">
              <span className="text-cyan-400">🌐</span> English
            </div>
          </div>
        </div>

        {dragOver && (
          <div className="absolute inset-0 bg-blue-500/20 border-2 border-dashed border-blue-400 rounded-xl flex items-center justify-center pointer-events-none">
            <div className="text-center">
              <SafeIcon icon={FiUpload} className="text-4xl text-blue-400 mb-2 mx-auto" />
              <p className="text-blue-400 font-semibold">Drop your chart images here!</p>
              <p className="text-blue-300 text-sm">Multiple files supported</p>
            </div>
          </div>
        )}
      </div>
      
      {showFullAnalysis && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-gray-800 rounded-xl w-full max-w-6xl max-h-[90vh] flex flex-col border border-gray-600">
            <div className="flex justify-between items-center p-6 border-b border-gray-700">
              <div>
                <span className="text-[10px] font-mono uppercase tracking-[0.25em] text-cyan-400">▸ ai output · full</span>
                <h2 className="text-2xl font-bold text-white flex items-center gap-3 mt-1">
                  <SafeIcon icon={FiBookOpen} className="text-cyan-400" />
                  Complete Professional Analysis
                  {uploadedImages.length > 0 && (
                    <span className="ml-1 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 text-[10px] font-mono uppercase tracking-wider font-bold">
                      {uploadedImages.length} chart{uploadedImages.length > 1 ? 's' : ''} analyzed
                    </span>
                  )}
                </h2>
              </div>
              <button 
                onClick={() => setShowFullAnalysis(false)}
                className="p-2 rounded-lg hover:bg-gray-700 text-gray-400 text-xl"
              >
                ✕
              </button>
            </div>
            
            <div className="flex-1 overflow-y-auto p-6">
              <div className="bg-gray-900/60 rounded-xl p-6 border border-white/10">
                <MarkdownText text={analysisText} />
              </div>
            </div>
            
            <div className="p-6 border-t border-gray-700 flex justify-between items-center">
              {/* Bouton style dashboard */}
              <button 
                onClick={handleCopy} 
                className="px-4 py-2.5 rounded-xl bg-gray-800/80 border border-purple-500/30 hover:bg-gray-700/80 hover:border-purple-500/50 transition-all flex items-center gap-2"
              >
                <div className="p-1.5 rounded-lg bg-gradient-to-r from-purple-600/30 to-cyan-600/30">
                  <SafeIcon icon={copied ? FiCheck : FiCopy} className="w-5 h-5 text-cyan-400" />
                </div>
                <span className="text-sm font-semibold text-white">{copied ? 'Copied!' : 'Copy Analysis'}</span>
              </button>
              
              {/* Info badges style dashboard */}
              <div className="flex items-center space-x-3">
                <div className="px-3 py-1.5 rounded-lg bg-gray-800/60 border border-white/10 text-sm text-gray-300 flex items-center gap-1.5">
                  <span className="text-cyan-400">🌐</span> English
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Branded Toast Notification */}
      {toast && (
        <div className="fixed bottom-6 right-6 z-50 animate-fade-in">
          <div className={`flex items-center gap-3 px-5 py-3.5 rounded-xl shadow-xl border backdrop-blur-sm ${
            toast.type === 'success' 
              ? 'bg-gradient-to-r from-emerald-900/90 to-cyan-900/90 border-emerald-500/40 text-emerald-200' 
              : 'bg-gradient-to-r from-red-900/90 to-orange-900/90 border-red-500/40 text-red-200'
          }`}>
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
              toast.type === 'success' 
                ? 'bg-emerald-500/20' 
                : 'bg-red-500/20'
            }`}>
              {toast.type === 'success' ? (
                <SafeIcon icon={FiCheck} className="w-4 h-4 text-emerald-400" />
              ) : (
                <SafeIcon icon={FiAlertCircle} className="w-4 h-4 text-red-400" />
              )}
            </div>
            <div>
              <div className="text-xs text-gray-400 font-medium mb-0.5">Snap Chart AI</div>
              <div className="text-sm font-medium">{toast.message}</div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AnalysisResult;